import { Controller } from '@hotwired/stimulus'

const SVG_NS = 'http://www.w3.org/2000/svg'

export default class extends Controller {
  static targets = ['dot', 'svg']
  static values = {
    mode: { type: String, default: 'processing' }
  }

  connect () {
    this.size = this.element.clientWidth || 180
    this.center = this.size / 2
    this.outerR = this.size * 0.355
    this.innerR = this.size * 0.1

    this.frame = null
    this.gradients = []
    this.lines = []

    this.setup()
    this.animate = this.animate.bind(this)
    this.frame = requestAnimationFrame(this.animate)
    this.svgTarget.setAttribute('viewBox', `0 0 ${this.size} ${this.size}`)
  }

  disconnect () {
    if (this.frame) cancelAnimationFrame(this.frame)
  }

  setup () {
    this.svgTarget.innerHTML = ''

    this.createGradients(this.dotTargets.length)
    this.createLines(this.dotTargets.length)

    this.seeds = this.dotTargets.map((_, i) => ({
      a: Math.random() * Math.PI * 2 + i,
      b: Math.random() * Math.PI * 2 + i * 0.7,
      c: Math.random() * Math.PI * 2 + i * 1.3,
      speed1: 0.6 + Math.random() * 0.7,
      speed2: 0.8 + Math.random() * 0.8,
      speed3: 0.4 + Math.random() * 0.9
    }))
  }

  createGradients (count) {
    const defs = document.createElementNS(SVG_NS, 'defs')
    this.svgTarget.appendChild(defs)

    this.gradients = []

    for (let i = 0; i < count; i++) {
      const gradient = document.createElementNS(SVG_NS, 'linearGradient')
      gradient.setAttribute('id', this.gradientId(i))
      gradient.setAttribute('gradientUnits', 'userSpaceOnUse')

      const stop1 = document.createElementNS(SVG_NS, 'stop')
      stop1.setAttribute('offset', '0%')

      const stop2 = document.createElementNS(SVG_NS, 'stop')
      stop2.setAttribute('offset', '100%')

      gradient.appendChild(stop1)
      gradient.appendChild(stop2)
      defs.appendChild(gradient)

      this.gradients.push(gradient)
    }
  }

  createLines (count) {
    this.lines = []

    for (let i = 0; i < count; i++) {
      const line = document.createElementNS(SVG_NS, 'line')
      line.setAttribute('class', 'status-loader__line')
      line.setAttribute('stroke', `url(#${this.gradientId(i)})`)
      this.svgTarget.appendChild(line)
      this.lines.push(line)
    }
  }

  gradientId (index) {
    return `status-loader-gradient-${this.identifier}-${this.element.dataset.statusLoaderId || this.element.dataset.controllerInstance || this.uid()}-${index}`
  }

  uid () {
    if (!this._uid) this._uid = Math.random().toString(36).slice(2, 11)
    return this._uid
  }

  animate (time) {
    const t = time * 0.001
    const points = []

    if (this.modeValue === 'processing') {
      this.dotTargets.forEach((dot, i) => {
        const seed = this.seeds[i]

        const angle =
          Math.sin(t * seed.speed1 + seed.a) * Math.PI +
          Math.cos(t * seed.speed2 + seed.b) * 0.9

        const radiusBase =
          24 + ((Math.sin(t * seed.speed3 + seed.c) + 1) * 0.5 * (this.outerR - 24))

        const jitter = Math.sin(t * 2.1 + i * 1.37) * 6
        const r = Math.max(18, Math.min(this.outerR, radiusBase + jitter))

        const x = this.center + Math.cos(angle) * r
        const y = this.center + Math.sin(angle * 1.13 + i * 0.25) * r * 0.92

        this.positionDot(dot, x, y)
        points.push({ x, y, color: this.dotColor(dot) })
      })

      this.updateLines(points, true)
    }

    if (this.modeValue === 'success') {
      const base = t * 1.25

      this.dotTargets.forEach((dot, i) => {
        const angle = base + (i * Math.PI / 2)
        const x = this.center + Math.cos(angle) * this.outerR
        const y = this.center + Math.sin(angle) * this.outerR

        this.positionDot(dot, x, y)
        points.push({ x, y, color: this.dotColor(dot) })
      })

      this.updateLines(points, true)
    }

    if (this.modeValue === 'failure') {
      this.dotTargets.forEach((dot, i) => {
        const angle = t * 0.9 + i * (Math.PI / 2)
        const pulse = 2.2 + Math.sin(t * 2.2 + i) * 1.2
        const r = this.innerR + pulse

        const x = this.center + Math.cos(angle) * r
        const y = this.center + Math.sin(angle) * r

        this.positionDot(dot, x, y)
      })

      this.updateLines([], false)
    }

    this.frame = requestAnimationFrame(this.animate)
  }

  updateLines (points, visible) {
    this.lines.forEach((line, i) => {
      if (!visible) {
        line.style.opacity = '0'
        return
      }

      const a = points[i]
      const b = points[(i + 1) % points.length]

      line.style.opacity = '0.95'
      line.setAttribute('x1', a.x)
      line.setAttribute('y1', a.y)
      line.setAttribute('x2', b.x)
      line.setAttribute('y2', b.y)

      this.setGradient(i, a.x, a.y, b.x, b.y, a.color, b.color)
    })
  }

  setGradient (index, x1, y1, x2, y2, c1, c2) {
    const gradient = this.gradients[index]
    if (!gradient) return

    gradient.setAttribute('x1', x1)
    gradient.setAttribute('y1', y1)
    gradient.setAttribute('x2', x2)
    gradient.setAttribute('y2', y2)
    gradient.children[0].setAttribute('stop-color', c1)
    gradient.children[1].setAttribute('stop-color', c2)
  }

  positionDot (dot, x, y) {
    dot.style.transform = `translate(${x - this.center}px, ${y - this.center}px)`
  }

  dotColor (dot) {
    return getComputedStyle(dot).backgroundColor
  }
}
